import React, { useRef, useState, useEffect } from "react";
import SignatureCanvas from "react-signature-canvas";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import html2pdf from "html2pdf.js";
import { useToast } from "@chakra-ui/react";
import client from "../../../../../helpers/Client";
import productService from "../../../../../services/productService";
import urlToId from "../../../../../helpers/UrlToId";

const INOHOM_LOGO_URL = "https://i.hizliresim.com/40dvsvk.png";
const RED = "#b00000";

const LINE_STYLE = {
  background: "rgba(176,0,0,0.13)",
  borderBottom: `1px solid ${RED}`,
  minHeight: "18px",
  display: "block",
  width: "100%",
};

const generateQRCode = (text) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(text)}`;

const inputStyle = {
  ...LINE_STYLE,
  flex: 1,
  fontSize: "9px",
  paddingLeft: "2px",
  border: "none",
  outline: "none",
  background: "rgba(176,0,0,0.15)",
  fontFamily: "Arial, sans-serif",
  color: "#111",
  minWidth: 0,
};

const textareaStyle = {
  width: "100%",
  background: "rgba(176,0,0,0.15)",
  border: "none",
  borderBottom: `1px solid ${RED}`,
  outline: "none",
  fontFamily: "Arial, sans-serif",
  fontSize: "9px",
  color: "#111",
  resize: "none",
  padding: "2px 4px",
  display: "block",
};

const cellStyle = {
  border: "1px solid #888",
  padding: "2px 3px",
  fontSize: "8px",
  textAlign: "center",
  background: "white",
};
const headerCellStyle = {
  ...cellStyle,
  background: "rgba(176,0,0,0.15)",
  fontWeight: "800",
  fontSize: "8px",
};

const opTypes = [
  "ARIZA ONARIM", "ÜRÜN DEĞİŞİMİ", "EK ÜRÜN MONTAJI",
  "SİSTEM DEVREYE ALMA", "YAZILIM GÜNCELLEME", "TEKNİK DESTEK", "EĞİTİM", "DİĞER"
];

const InlineInput = ({ value, onChange, style = {}, placeholder = "" }) => (
  <input
    value={value}
    onChange={e => onChange(e.target.value)}
    placeholder={placeholder}
    style={{ ...inputStyle, ...style }}
  />
);

const TicketPrintModal = ({ isOpen, onClose, ticket, onConfirmService }) => {
  const customerSigRef = useRef(null);
  const technicianSigRef = useRef(null);
  const [signatures, setSignatures] = useState({ customer: null, technician: null });
  const [isConfirming, setIsConfirming] = useState(false);
  const [products, setProducts] = useState([]);
  const toast = useToast();

  const [formData, setFormData] = useState({
    serial: "",
    project_no: "",
    site_name: "",
    flat_no: "",
    service_duration: "",
    service_bayi: "",
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_address: "",
    service_reason: "",
    fault_codes: "",
    work_done: "",
    service_fee: "",
    payment_method: "Nakit",
    checked_ops: [],
    need_revisit: null,
    delivered_working: null,
    rows: [],
  });

  useEffect(() => {
    if (ticket) {
      setFormData(prev => ({
        ...prev,
        serial: ticket.number || "",
        project_no: ticket?.related_unit?.name || "",
        site_name: ticket.site_name || "",
        flat_no: ticket.flat_no || "",
        service_bayi: ticket?.assigned_to?.name || "",
        customer_name: ticket.full_name || ticket.customer?.name || "",
        customer_email: ticket.email || "",
        customer_phone: ticket.phone_number || ticket.customer?.phone || "",
        customer_address: ticket.address || ticket.customer?.address || "",
        service_reason: ticket.body || "",
        work_done: ticket.title || "",
      }));
    }
  }, [ticket]);

  useEffect(() => {
    if (isOpen) {
      productService.getProducts().then(data => {
        if (Array.isArray(data)) {
          setProducts(data);
        } else if (data?.results) {
          setProducts(data.results);
        } else {
          setProducts([]);
        }
      }).catch(() => setProducts([]));
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !ticket) return;
    if (!Array.isArray(ticket.tasks) || ticket.tasks.length === 0) return;

    let alive = true;

    const toNumber = (value) => {
      if (value == null) return 0;
      const normalized = String(value).replace(",", ".").trim();
      const n = Number(normalized);
      return Number.isFinite(n) ? n : 0;
    };

    const extractModelInfo = (record) => {
      const pm = record?.product_model || record?.related_model || null;
      if (!pm) return { name: "", code: "" };
      if (typeof pm === "object") {
        return {
          name: pm.name || pm.model_name || pm.title || "",
          code: pm.model_code || pm.code || "",
        };
      }
      const matched = (products || []).find((p) => p?.url === pm || String(p?.id) === String(pm));
      return {
        name: matched?.name || matched?.model_name || matched?.title || "",
        code: matched?.model_code || matched?.code || "",
      };
    };

    const fetchSubtaskServiceRecords = async () => {
      const tasks = Array.isArray(ticket.tasks) ? ticket.tasks : [];
      const subtasks = tasks.filter((task) => !!task?.parent_task);
      const taskIds = Array.from(
        new Set((subtasks.length ? subtasks : tasks).map((t) => t?.id).filter(Boolean))
      );
      if (!taskIds.length) return;

      const allServiceRecords = await Promise.all(
        taskIds.map((taskId) =>
          client
            .get(`service_records/?task=${taskId}`)
            .then((res) => (Array.isArray(res.data) ? res.data : Array.isArray(res.data?.results) ? res.data.results : []))
            .catch(() => [])
        )
      );

      if (!alive) return;

      const records = allServiceRecords.flat().filter(Boolean);
      if (!records.length) return;

      const mergeText = (base, extra) => {
        const b = String(base || "").trim();
        const e = String(extra || "").trim();
        if (!e) return b;
        if (!b) return e;
        if (b.includes(e)) return b;
        return `${b}\n${e}`;
      };

      const rowsFromRecords = records
        .map((r) => {
          const { name } = extractModelInfo(r);
          if (!name) return null;
          const unitPrice = toNumber(r?.price);
          return { id: uuidv4(), item: name, qty: 1, unit_price: unitPrice, total: unitPrice, action: r?.service_type || "" };
        })
        .filter(Boolean);

      const codes = Array.from(
        new Set(
          records
            .map((r) => extractModelInfo(r).code)
            .filter((c) => String(c || "").trim())
        )
      );

      const descText = records
        .map((r) => {
          const modelName = String(extractModelInfo(r).name || "").trim();
          const desc = String(r?.desc || "").trim();
          const sdesc = String(r?.service_desc || "").trim();
          if (!desc) return "";
          if (sdesc && desc === sdesc) return "";
          return modelName ? `${modelName} --- ${desc}` : desc;
        })
        .filter(Boolean)
        .join("\n");

      const serviceDescText = records
        .map((r) => {
          const modelName = String(extractModelInfo(r).name || "").trim();
          const sdesc = String(r?.service_desc || "").trim();
          if (!sdesc) return "";
          return modelName ? `${modelName} --- ${sdesc}` : sdesc;
        })
        .filter(Boolean)
        .join("\n");

      const feeSum = records.reduce((acc, r) => acc + toNumber(r?.price), 0);

      setFormData((prev) => {
        const next = { ...prev };
        if ((!Array.isArray(prev.rows) || prev.rows.length === 0) && rowsFromRecords.length) next.rows = rowsFromRecords;
        if (!String(prev.fault_codes || "").trim() && codes.length) next.fault_codes = codes.join(", ");
        if (descText) next.service_reason = mergeText(prev.service_reason, descText);
        if (serviceDescText) next.work_done = mergeText(prev.work_done, serviceDescText);
        if (!String(prev.service_fee || "").trim() && feeSum > 0) next.service_fee = String(feeSum.toFixed(2));
        return next;
      });
    };

    fetchSubtaskServiceRecords().catch(() => { });

    return () => {
      alive = false;
    };
  }, [isOpen, ticket, products]);

  const update = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));

  const toggleOp = (op) => {
    setFormData(prev => ({
      ...prev,
      checked_ops: prev.checked_ops.includes(op)
        ? prev.checked_ops.filter(o => o !== op)
        : [...prev.checked_ops, op],
    }));
  };

  const saveSignatureImages = () => {
    setSignatures({
      customer: customerSigRef.current?.isEmpty() ? null : customerSigRef.current?.toDataURL(),
      technician: technicianSigRef.current?.isEmpty() ? null : technicianSigRef.current?.toDataURL(),
    });
  };

  const addRow = () => update("rows", [...formData.rows, { id: uuidv4(), item: "", qty: 1, unit_price: 0, total: 0, action: "" }]);
  const updateRow = (idx, field, value) => {
    const rows = [...formData.rows];
    rows[idx] = { ...rows[idx], [field]: value };
    if (field === "qty" || field === "unit_price") {
      const qty = Number(rows[idx].qty) || 0;
      const unitPrice = Number(rows[idx].unit_price) || 0;
      rows[idx].total = qty * unitPrice;
    }
    update("rows", rows);
  };
  const removeRow = (idx) => update("rows", formData.rows.filter((_, i) => i !== idx));

  const handlePrint = () => window.print();

  const makePdfWorker = (element, { filename } = {}) => {
    const opt = {
      margin: 10,
      filename,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        scrollY: 0,
        letterRendering: true,
        onclone: (doc) => {
          try {
            doc.body.classList.add("pdf-export");
          } catch (_) { }
          const area = doc.getElementById("ticket-print-area");
          if (!area) return;
          const win = doc.defaultView;
          if (!win) return;

          const replaceWithText = (el, text, { whiteSpace } = {}) => {
            const cs = win.getComputedStyle(el);
            const span = doc.createElement("span");
            span.textContent = text || "";
            span.style.display = cs.display === "block" ? "block" : "inline-block";
            span.style.whiteSpace = whiteSpace || "pre";
            span.style.fontFamily = cs.fontFamily;
            span.style.fontSize = cs.fontSize;
            span.style.fontWeight = cs.fontWeight;
            span.style.letterSpacing = cs.letterSpacing;
            span.style.lineHeight = cs.lineHeight;
            span.style.color = cs.color;
            span.style.textAlign = cs.textAlign;
            span.style.padding = cs.padding;
            span.style.margin = cs.margin;
            span.style.border = cs.border;
            span.style.borderBottom = cs.borderBottom;
            span.style.borderTop = cs.borderTop;
            span.style.borderLeft = cs.borderLeft;
            span.style.borderRight = cs.borderRight;
            span.style.borderRadius = cs.borderRadius;
            span.style.backgroundColor = cs.backgroundColor;
            span.style.boxSizing = cs.boxSizing;
            span.style.width = cs.width;
            span.style.minWidth = cs.minWidth;
            span.style.maxWidth = cs.maxWidth;
            span.style.height = cs.height;
            span.style.verticalAlign = cs.verticalAlign;
            span.style.overflow = "hidden";
            el.replaceWith(span);
          };

          area.querySelectorAll("input").forEach((el) => {
            const type = (el.getAttribute("type") || "text").toLowerCase();
            if (type === "checkbox" || type === "radio") {
              replaceWithText(el, el.checked ? "☑" : "☐");
              return;
            }
            replaceWithText(el, el.value || "");
          });
          area.querySelectorAll("textarea").forEach((el) => {
            replaceWithText(el, el.value || "", { whiteSpace: "pre-wrap" });
          });
          area.querySelectorAll("select").forEach((el) => {
            const text = el.options?.[el.selectedIndex]?.text || "";
            replaceWithText(el, text);
          });
        },
      },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };
    if (!opt.filename) delete opt.filename;
    return html2pdf().from(element).set(opt);
  };

  const handlePdf = async () => {
    const element = document.getElementById("ticket-print-area");
    if (!element) return;
    await makePdfWorker(element, { filename: `servis_formu_${ticket?.number || "yeni"}.pdf` }).save();
  };

  const createServicePdfBlob = async () => {
    const element = document.getElementById("ticket-print-area");
    if (!element) throw new Error("ticket-print-area bulunamadı");
    const worker = makePdfWorker(element);
    const blob = await worker.toPdf().outputPdf("blob");
    if (!(blob instanceof Blob)) throw new Error("PDF blob oluşturulamadı");
    return blob;
  };

  const uploadToFilepond = async (file) => {
    const fd = new FormData();
    fd.append("filepond", file, file.name);
    fd.append("upload_field_name", "filepond");
    const resp = await client.post("/fp/process/", fd, {
      params: { upload_field_name: "filepond" },
      headers: { "Content-Type": "multipart/form-data" },
    });
    const raw = resp?.data;
    const id =
      typeof raw === "string"
        ? String(raw).replace(/^"+|"+$/g, "").trim()
        : raw?.id || raw?.upload_id || raw?.serverId || raw?.server_id;
    if (!id) throw new Error("Upload ID alınamadı");
    return id;
  };

  const handleConfirmService = async () => {
    setIsConfirming(true);
    try {
      const enrich = (label, err) => {
        const url = err?.config?.url ? String(err.config.url) : "";
        const status = err?.response?.status;
        const data = err?.response?.data;
        const dataText =
          typeof data === "string"
            ? data.slice(0, 500)
            : data
              ? JSON.stringify(data).slice(0, 500)
              : "";
        const msg = [label, status ? `HTTP ${status}` : null, url || null, dataText || null]
          .filter(Boolean)
          .join(" - ");
        const e = new Error(msg || label);
        e.cause = err;
        return e;
      };

      const ticketIdRaw = urlToId(ticket?.url) || ticket?.id;
      const ticketId = ticketIdRaw ? Number(ticketIdRaw) : null;
      if (!ticketId || !Number.isFinite(ticketId)) throw new Error("Ticket ID bulunamadı");

      const pdfBlob = await createServicePdfBlob();
      const uniqueSuffix = moment().format("YYYYMMDD_HHmmss");
      const fileName = `servis_formu_${ticket?.number || ticketId}_${uniqueSuffix}.pdf`;
      const file = new File([pdfBlob], fileName, { type: "application/pdf" });
      let uploadId;
      try {
        uploadId = await uploadToFilepond(file);
      } catch (e) {
        throw enrich("PDF yükleme (fp/process) başarısız", e);
      }
      try {
        await client.post("/attachments/", {
          upload_id: uploadId,
          description: `Servis formu PDF (İş Emri #${ticket?.number || ticketId})`,
          object_model: "ticket.ticket",
          object_id: ticketId,
        });
      } catch (e) {
        throw enrich("PDF eklenti kaydı (/attachments) başarısız", e);
      }

      toast({
        title: "Başarılı",
        description: "PDF eklentisi oluşturuldu.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      if (onConfirmService) await onConfirmService({ ticketId });
      onClose();
    } catch (e) {
      console.error(e);
      toast({
        title: "Hata",
        description: e?.response?.data?.detail || e?.message || "PDF oluşturulamadı",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsConfirming(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1400, background: "rgba(0,0,0,0.6)", overflowY: "auto" }}>
      <div style={{ background: "white", width: "100%", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        {/* HEADER */}
        <div style={{ borderBottom: "1px solid #e2e8f0", padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "white", position: "sticky", top: 0, zIndex: 10 }}>
          <div>
            <span style={{ fontWeight: "bold", fontSize: "18px" }}>Servis Formu</span>
            <span style={{ marginLeft: "12px", fontSize: "12px", color: "#888" }}>#{ticket?.number}</span>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <button onClick={handleConfirmService} disabled={isConfirming} style={{ background: "#16a34a", color: "white", border: "none", borderRadius: "6px", padding: "8px 16px", cursor: "pointer", fontWeight: "600" }}>
              {isConfirming ? "Onaylanıyor..." : "✓ Onayla"}
            </button>
            <button onClick={handlePrint} style={{ background: "#2563eb", color: "white", border: "none", borderRadius: "6px", padding: "8px 16px", cursor: "pointer", fontWeight: "600" }}>
              🖨 Yazdır
            </button>
            <button onClick={handlePdf} style={{ background: "white", border: "1px solid #ccc", borderRadius: "6px", padding: "8px 16px", cursor: "pointer", fontWeight: "600" }}>
              ⬇ PDF
            </button>
            <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#666" }}>&times;</button>
          </div>
        </div>

        {/* A4 PREVIEW */}
        <div style={{ background: "#f3f4f6", padding: "24px", flex: 1 }}>
          <div
            id="ticket-print-area"
            style={{ background: "white", width: "270mm", minHeight: "370mm", margin: "0 auto", padding: "13mm 16mm", fontFamily: "Arial, sans-serif", color: "#111", boxSizing: "border-box", fontSize: "13px", boxShadow: "0 4px 24px rgba(0,0,0,0.12)" }}
          >
            <style>{`
              @media print {
                @page { size: A4; margin: 10mm; }
                body * { visibility: hidden; }
                #ticket-print-area, #ticket-print-area * { visibility: visible; }
                #ticket-print-area {
                  position: fixed !important;
                  top: 0 !important;
                  left: 0 !important;
                  width: 190mm !important;
                  min-height: unset !important;
                  padding: 0 !important;
                  margin: 0 !important;
                  box-shadow: none !important;
                  font-size: 10px !important;
                  transform: none !important;
                }
                .no-print { display: none !important; }
                * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
              }
              body.pdf-export #ticket-print-area {
                position: relative !important;
                width: 190mm !important;
                min-height: unset !important;
                padding: 0 !important;
                margin: 0 auto !important;
                box-shadow: none !important;
                font-size: 10px !important;
                transform: none !important;
              }
              body.pdf-export .no-print { display: none !important; }
            `}</style>

            {/* ===== HEADER ===== */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "6px" }}>
              <div>
                <img src={INOHOM_LOGO_URL} alt="inohom" style={{ height: "44px", objectFit: "contain", display: "block" }} />
              </div>
              <div style={{ textAlign: "center", flex: 1, paddingTop: "4px" }}>
                <span style={{ fontSize: "14px", fontWeight: "900", letterSpacing: "1px" }}>SERVİS FORMU</span>
              </div>
              <div style={{ textAlign: "right" }}>
                <span style={{ color: RED, fontWeight: "900", fontSize: "16px", letterSpacing: "1px" }}>
                  SERİ: <input value={formData.serial} onChange={e => update("serial", e.target.value)} style={{ color: RED, fontWeight: "900", fontSize: "14px", border: "none", borderBottom: `1px solid ${RED}`, outline: "none", width: "80px", background: "transparent", textAlign: "right" }} />
                </span>
              </div>
            </div>

            {/* ===== MÜŞTERİ & SERVİS BİLGİLERİ ===== */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px", marginBottom: "6px" }}>
              <div>
                {[
                  ["MÜŞTERİ PROJE NO", "project_no"],
                  ["ADI-SOYADI", "customer_name"],
                  ["SİTE/APARTMAN ADI", "site_name"],
                  ["DAİRE NO", "flat_no"],
                  ["ADRES", "customer_address"],
                  ["E-POSTA", "customer_email"],
                  ["TELEFON", "customer_phone"],
                ].map(([label, key]) => (
                  <div key={key} style={{ display: "flex", alignItems: "flex-end", marginBottom: "4px", gap: "6px" }}>
                    <span style={{ fontSize: "9px", fontWeight: "700", whiteSpace: "nowrap", minWidth: "110px", textAlign: "right" }}>{label}</span>
                    <InlineInput value={formData[key]} onChange={v => update(key, v)} />
                  </div>
                ))}
              </div>
              <div style={{ paddingTop: "22px" }}>
                {[
                  ["SERVİS TARİHİ", moment().format("DD.MM.YYYY"), null],
                  ["SERVİS SAATİ", moment().format("HH:mm"), null],
                  ["SERVİS SÜRESİ", formData.service_duration, "service_duration"],
                  [null, null, null], // spacer
                  ["SERVİS KAYIT NO", ticket?.number, null],
                  ["SERVİS BAYİSİ", formData.service_bayi, "service_bayi"],
                ].map(([label, val, key], i) => {
                  if (!label) return <div key={i} style={{ height: "22px" }} />;
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "flex-end", marginBottom: "4px", gap: "6px" }}>
                      <span style={{ fontSize: "9px", fontWeight: "700", whiteSpace: "nowrap" }}>{label}</span>
                      {key ? (
                        <InlineInput value={formData[key]} onChange={v => update(key, v)} />
                      ) : (
                        <span style={{ ...LINE_STYLE, flex: 1, fontSize: "9px", paddingLeft: "2px" }}>{val}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ===== SERVİS NEDENİ + OPERASYON TİPLERİ ===== */}
            <div style={{ borderTop: `3px solid ${RED}`, borderBottom: `3px solid ${RED}`, marginBottom: "4px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 130px" }}>
                <div style={{ borderRight: "1px solid #aaa", paddingRight: "6px", paddingTop: "3px", paddingBottom: "3px" }}>
                  <div style={{ display: "flex", gap: "6px", alignItems: "flex-start" }}>
                    <span style={{ fontSize: "8px", fontWeight: "800", whiteSpace: "nowrap", paddingTop: "2px" }}>SERVİS NEDENİ </span>
                    <span style={{ fontSize: "8px", fontWeight: "800", whiteSpace: "nowrap", paddingTop: "2px" }}> </span>
                    <span style={{ fontSize: "8px", fontWeight: "800", whiteSpace: "nowrap", paddingRight: "21px" }}> </span>
                    <textarea
                      value={formData.service_reason}
                      onChange={e => update("service_reason", e.target.value)}
                      rows={4}
                      style={{ ...textareaStyle, flex: 1 }}
                    />
                  </div>
                  <div style={{ display: "flex", gap: "6px", alignItems: "flex-start", marginTop: "4px" }}>
                    <span style={{ fontSize: "8px", fontWeight: "800", whiteSpace: "nowrap", paddingTop: "2px" }}>SİSTEM ARIZA KODLARI</span>
                    <InlineInput value={formData.fault_codes} onChange={v => update("fault_codes", v)} />
                  </div>
                </div>
                <div style={{ paddingLeft: "4px", paddingTop: "2px", paddingBottom: "2px" }}>
                  {opTypes.map((type) => (
                    <div key={type} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2px" }}>
                      <span style={{ fontSize: "7.5px", fontWeight: "700" }}>{type}</span>
                      <input
                        type="checkbox"
                        checked={formData.checked_ops.includes(type)}
                        onChange={() => toggleOp(type)}
                        style={{ width: "12px", height: "12px", cursor: "pointer", accentColor: RED, flexShrink: 0 }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ===== YAPILAN İŞLEM AÇIKLAMASI ===== */}
            <div style={{ borderBottom: `3px solid ${RED}`, marginBottom: "4px" }}>
              <div style={{ textAlign: "center", fontSize: "9px", fontWeight: "800", padding: "3px 0" }}>YAPILAN İŞLEM AÇIKLAMASI</div>
              <textarea
                value={formData.work_done}
                onChange={e => update("work_done", e.target.value)}
                rows={5}
                style={{ ...textareaStyle, lineHeight: "18px" }}
              />
            </div>

            {/* ===== SERVİS VERİLEN ÜRÜNLER TABLOSU ===== */}
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "6px", fontSize: "8px" }}>
              <thead>
                <tr>
                  <th colSpan={5} style={{ ...headerCellStyle, borderBottom: "none" }}>SERVİS VERİLEN ÜRÜNLER</th>
                  <th colSpan={4} style={{ ...headerCellStyle, borderBottom: "none" }}>YAPILAN İŞLEM</th>
                </tr>
                <tr>
                  <th style={headerCellStyle}>#</th>
                  <th style={{ ...headerCellStyle, minWidth: "80px" }}>ÜRÜN / PARÇA ADI</th>
                  <th style={headerCellStyle}>ADET</th>
                  <th style={headerCellStyle}>BİRİM FİYAT</th>
                  <th style={headerCellStyle}>TOPLAM</th>
                  <th style={headerCellStyle}>TAMİR</th>
                  <th style={headerCellStyle}>DEĞİŞİM</th>
                  <th style={headerCellStyle}>İKAME</th>
                  <th style={{ ...headerCellStyle }} className="no-print">SİL</th>
                </tr>
              </thead>
              <tbody>
                {formData.rows.map((r, idx) => (
                  <tr key={r.id}>
                    <td style={{ ...cellStyle, width: "18px", fontWeight: "700" }}>{idx + 1}</td>
                    <td style={{ ...cellStyle, textAlign: "left" }}>
                      <select value={r.item} onChange={e => updateRow(idx, "item", e.target.value)} style={{ border: "none", outline: "none", width: "100%", fontSize: "8px", background: "transparent", fontFamily: "Arial", cursor: "pointer" }}>
                        <option value="">-- Seçin --</option>
                        {products.map(p => (
                          <option key={p.id} value={p.name || p.product_name || p.title || String(p.id)}>
                            {p.name || p.product_name || p.title || `Ürün #${p.id}`}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={cellStyle}>
                      <input type="number" value={r.qty} onChange={e => updateRow(idx, "qty", e.target.value)} style={{ border: "none", outline: "none", width: "30px", fontSize: "8px", background: "transparent", textAlign: "center", fontFamily: "Arial" }} />
                    </td>
                    <td style={cellStyle}>
                      <input type="number" value={r.unit_price} onChange={e => updateRow(idx, "unit_price", e.target.value)} style={{ border: "none", outline: "none", width: "40px", fontSize: "8px", background: "transparent", textAlign: "center", fontFamily: "Arial" }} />
                    </td>
                    <td style={{ ...cellStyle, fontWeight: "700" }}>{r.total} ₺</td>
                    <td style={cellStyle}>
                      <input
                        type="checkbox"
                        checked={r.action === "renovation"}
                        onChange={() => updateRow(idx, "action", r.action === "renovation" ? "" : "renovation")}
                        style={{ accentColor: RED, cursor: "pointer" }}
                      />
                    </td>
                    <td style={cellStyle}>
                      <input
                        type="checkbox"
                        checked={r.action === "replacement"}
                        onChange={() => updateRow(idx, "action", r.action === "replacement" ? "" : "replacement")}
                        style={{ accentColor: RED, cursor: "pointer" }}
                      />
                    </td>
                    <td style={cellStyle}>
                      <input
                        type="checkbox"
                        checked={r.action === "substitute"}
                        onChange={() => updateRow(idx, "action", r.action === "substitute" ? "" : "substitute")}
                        style={{ accentColor: RED, cursor: "pointer" }}
                      />
                    </td>
                    <td style={{ ...cellStyle }} className="no-print">
                      <button onClick={() => removeRow(idx)} style={{ background: "none", border: "none", cursor: "pointer", color: "red", fontSize: "12px" }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button className="no-print" onClick={addRow} style={{ fontSize: "8px", padding: "3px 8px", marginBottom: "6px", border: "1px solid #aaa", borderRadius: "4px", cursor: "pointer", background: "#f9f9f9" }}>+ Satır Ekle</button>

            {/* ===== ALT BÖLÜM ===== */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", border: "1px solid #aaa" }}>
              {/* Sol */}
              <div style={{ padding: "6px", borderRight: "1px solid #aaa" }}>
                <div style={{ marginBottom: "8px" }}>
                  <div style={{ fontSize: "8px", fontWeight: "700", marginBottom: "4px" }}>Servis tekrar ihtiyacı var mı?</div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    {["Hayır", "Evet"].map(opt => (
                      <label key={opt} style={{ display: "flex", alignItems: "center", gap: "3px", cursor: "pointer" }}>
                        <input type="radio" name="revisit" checked={formData.need_revisit === opt} onChange={() => update("need_revisit", opt)} style={{ accentColor: RED }} />
                        <span style={{ fontSize: "8px", fontWeight: "700" }}>{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: "12px" }}>
                  <div style={{ fontSize: "8px", fontWeight: "700", marginBottom: "4px" }}>Sistem çalışır durumda teslim edildi</div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    {["Hayır", "Evet"].map(opt => (
                      <label key={opt} style={{ display: "flex", alignItems: "center", gap: "3px", cursor: "pointer" }}>
                        <input type="radio" name="delivered" checked={formData.delivered_working === opt} onChange={() => update("delivered_working", opt)} style={{ accentColor: RED }} />
                        <span style={{ fontSize: "8px", fontWeight: "700" }}>{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #aaa", paddingBottom: "3px", marginBottom: "3px", alignItems: "center" }}>
                    <span style={{ fontSize: "8px", fontWeight: "700" }}>Servis Tutarı</span>
                    <input type="number" value={formData.service_fee} onChange={e => update("service_fee", e.target.value)} style={{ border: "none", borderBottom: "1px solid #ccc", outline: "none", width: "60px", fontSize: "8px", textAlign: "right", background: "transparent", fontFamily: "Arial" }} />
                    <span style={{ fontSize: "8px" }}>₺</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: "8px", fontWeight: "700" }}>Ödeme Şekli</span>
                    <select value={formData.payment_method} onChange={e => update("payment_method", e.target.value)} style={{ fontSize: "8px", border: "1px solid #ccc", background: "white", fontFamily: "Arial", padding: "1px 2px" }}>
                      <option>Nakit</option>
                      <option>Kredi Kartı</option>
                      <option>Havale/EFT</option>
                      <option>Diğer</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Teknisyen */}
              <div style={{ padding: "6px", borderRight: "1px solid #aaa", textAlign: "center" }}>
                <div style={{ fontSize: "8px", fontWeight: "800", background: "rgba(176,0,0,0.15)", padding: "3px", marginBottom: "6px" }}>TEKNİK SERVİS PERSONELİ</div>
                <div className="no-print" style={{ fontSize: "7px", color: "#888", marginBottom: "4px" }}>İmza çizmek için tıklayın</div>
                <div style={{ border: "1px dashed #aaa", overflow: "hidden", marginBottom: "4px" }}>
                  <SignatureCanvas ref={technicianSigRef} penColor="black" backgroundColor="rgba(0,0,0,0)" canvasProps={{ width: 300, height: 80, style: { width: "100%", height: "80px" } }} onEnd={saveSignatureImages} />
                </div>
                <button className="no-print" onClick={() => { technicianSigRef.current?.clear(); saveSignatureImages(); }} style={{ fontSize: "7px", border: "1px solid #ccc", borderRadius: "3px", padding: "1px 6px", cursor: "pointer", background: "#f9f9f9", marginBottom: "4px" }}>Temizle</button>
                <div style={{ borderTop: "1px solid #aaa", paddingTop: "4px", marginTop: "4px" }}>
                  <div style={{ fontSize: "9px", fontWeight: "700" }}>{ticket?.assigned_to?.name || "Adı Soyadı"}</div>
                  <div style={{ fontSize: "8px", color: "#555" }}>İmza</div>
                </div>
              </div>

              {/* Müşteri */}
              <div style={{ padding: "6px", textAlign: "center" }}>
                <div style={{ fontSize: "8px", fontWeight: "800", background: "rgba(176,0,0,0.15)", padding: "3px", marginBottom: "6px" }}>MÜŞTERİ</div>
                <div className="no-print" style={{ fontSize: "7px", color: "#888", marginBottom: "4px" }}>İmza çizmek için tıklayın</div>
                <div style={{ border: "1px dashed #aaa", overflow: "hidden", marginBottom: "4px" }}>
                  <SignatureCanvas ref={customerSigRef} penColor="black" backgroundColor="rgba(0,0,0,0)" canvasProps={{ width: 300, height: 80, style: { width: "100%", height: "80px" } }} onEnd={saveSignatureImages} />
                </div>
                <button className="no-print" onClick={() => { customerSigRef.current?.clear(); saveSignatureImages(); }} style={{ fontSize: "7px", border: "1px solid #ccc", borderRadius: "3px", padding: "1px 6px", cursor: "pointer", background: "#f9f9f9", marginBottom: "4px" }}>Temizle</button>
                <div style={{ borderTop: "1px solid #aaa", paddingTop: "4px", marginTop: "4px" }}>
                  <div style={{ fontSize: "9px", fontWeight: "700" }}>{formData.customer_name || "Adı Soyadı"}</div>
                  <div style={{ fontSize: "8px", color: "#555" }}>İmza</div>
                </div>
              </div>
            </div>

            {/* ===== COMPANY FOOTER ===== */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #aaa", marginTop: "6px", paddingTop: "4px" }}>
              <span style={{ fontSize: "8px" }}>www.inohom.com</span>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>

                <span style={{ fontSize: "8px", fontWeight: "700" }}>KVKK kapsamındaki verilerinize ilişkin bilgilere inohom.com üzerinden ulaşabilirsiniz.</span>
              </div>
              <span style={{ fontSize: "8px", fontWeight: "700" }}>GST Elektronik A.Ş.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketPrintModal;
